import { useEffect, useRef, useState } from 'react';
import { fetchReplay, recordCtaClick, type Project, type ReplayView } from '../api';
import { formatSession } from '../format';
import { Header } from './register';

/**
 * Die Aufzeichnung (Konzept 3.9 „Nach dem Ende"): der Link aus der Nachfassmail. Anders als der
 * Raum läuft hier nichts nach der Uhr – mit Bedienleiste, ohne Gerätewache, ohne Anschau-Meldung.
 * Sie gilt nur so lange, wie das Webinar es erlaubt; danach bleibt der Weg zu einem neuen Termin.
 */
export function ReplayPage({ project, token }: { project: Project; token: string }) {
  const [view, setView] = useState<ReplayView | null | undefined>(undefined);
  useEffect(() => {
    fetchReplay(token)
      .then((v) => {
        setView(v);
        document.title = `Aufzeichnung: ${v.webinar.title} – ${project.name}`;
      })
      .catch(() => setView(null));
  }, [token, project.name]);

  if (view === undefined) return null;
  if (view === null) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Diesen Zugang gibt es nicht</h1>
          <p className="muted">Bitte prüfe den Link aus deiner E-Mail.</p>
        </main>
      </>
    );
  }

  const greeting = view.registration.name ? `, ${view.registration.name}` : '';
  return (
    <>
      <Header project={project} right={<span className="pill">Aufzeichnung</span>} />
      <main className="page page--room">
        <p className="label">{view.webinar.title}</p>
        {view.state === 'available' && (
          <>
            <h1 className="room__title">Die Aufzeichnung{greeting}</h1>
            {view.video ? <ReplayVideo hlsUrl={view.video.hlsUrl} posterUrl={view.video.posterUrl} /> : <p className="muted">Das Video wird gerade aufbereitet – bitte schau in ein paar Minuten noch einmal vorbei.</p>}
            {view.validUntil && <p className="muted small">Verfügbar bis {formatSession(view.validUntil, project.timezone)}.</p>}
            {view.cta && (
              <div className="offer">
                <div className="offer__body">
                  <span className="label label--brand">Zum Webinar passend</span>
                  <h2 className="offer__title">{view.cta.headline}</h2>
                  {view.cta.text && <p className="muted">{view.cta.text}</p>}
                </div>
                <div className="offer__action">
                  <a className="button" href={view.cta.url} target="_blank" rel="noopener" onClick={() => void recordCtaClick(token).catch(() => undefined)}>
                    {view.cta.buttonLabel}
                  </a>
                </div>
              </div>
            )}
          </>
        )}
        {view.state === 'not_yet' && (
          <>
            <h1 className="room__title">Die Aufzeichnung kommt nach dem Termin</h1>
            <p className="muted">
              Dein Termin: {formatSession(view.session.startsAt, project.timezone)}. <a href={`/raum/${token}`}>Zum Webinar-Raum</a>
            </p>
          </>
        )}
        {view.state === 'expired' && (
          <>
            <h1 className="room__title">Die Aufzeichnung ist nicht mehr verfügbar</h1>
            <p className="muted">
              Sie galt bis {view.validUntil ? formatSession(view.validUntil, project.timezone) : '–'}. Du kannst dir aber einen neuen Termin aussuchen: <a href={`/umbuchen/${token}`}>Neuen Termin wählen</a>
            </p>
          </>
        )}
        {view.state === 'unavailable' && (
          <>
            <h1 className="room__title">Zu diesem Webinar gibt es keine Aufzeichnung</h1>
            <p className="muted">
              Es wird nur zu festen Terminen gezeigt. <a href={`/umbuchen/${token}`}>Neuen Termin wählen</a>
            </p>
          </>
        )}
      </main>
    </>
  );
}

/** Schlichter Player mit Bedienleiste: HLS über hls.js, Safari kann es von Haus aus. */
function ReplayVideo({ hlsUrl, posterUrl }: { hlsUrl: string; posterUrl: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      return;
    }
    let destroy = () => undefined as void;
    void import('hls.js').then(({ default: HlsLib }) => {
      if (!HlsLib.isSupported()) return;
      const hls = new HlsLib({ capLevelToPlayerSize: true, maxBufferLength: 30, maxMaxBufferLength: 90 });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      destroy = () => hls.destroy();
    });
    return () => destroy();
  }, [hlsUrl]);
  return (
    <div className="player">
      <video ref={videoRef} controls playsInline poster={posterUrl} preload="metadata" style={{ width: '100%', height: '100%', display: 'block', background: 'var(--video-bg)' }} />
    </div>
  );
}
